import { useMemo, useState } from 'react';
import { ChecklistHeader } from '../components/checklist/ChecklistHeader';
import { EmptyState } from '../components/EmptyState';
import { TodayView } from '../components/checklist/TodayView';
import { WeekGrid } from '../components/checklist/WeekGrid';
import { useChecklistToday, useChecklistWeek, useCompleteTask, type ChecklistEntry } from '../lib/checklist';
import { dailyKey, formatWeekRange, shiftWeek, weekDates, weeklyKey } from '../lib/period';
import { useReferenceTasks } from '../lib/reference';

type MobileTab = 'today' | 'week';

const tabClasses = (active: boolean) =>
  [
    'min-h-tap flex-1 rounded-lg text-sm font-semibold transition-colors',
    active ? 'bg-accent text-accent-contrast' : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
  ].join(' ');

export function Checklist() {
  const now = new Date();
  const todayIso = dailyKey(now);
  const currentWeekKeyIso = weeklyKey(now);

  const [weekKey, setWeekKey] = useState(currentWeekKeyIso);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [mobileTab, setMobileTab] = useState<MobileTab>('today');

  const days = useMemo(() => weekDates(weekKey), [weekKey]);
  const weekQueries = useChecklistWeek(days);
  const todayQuery = useChecklistToday();
  const { data: referenceTasks } = useReferenceTasks();
  const completeTask = useCompleteTask();

  const taskCategoryBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const task of referenceTasks ?? []) {
      map.set(task.slug, task.category);
    }
    return map;
  }, [referenceTasks]);

  const dayColumns = days.map((iso, index) => ({
    iso,
    response: weekQueries[index]?.data,
    isPending: weekQueries[index]?.isPending ?? false,
    isFuture: iso > todayIso,
  }));

  // All of the user's accounts are always listed by GET /api/checklist, even ones with nothing
  // enabled yet — so the switcher doesn't need a separate /api/accounts call.
  const referenceResponse = [...dayColumns].reverse().find((d) => d.response)?.response ?? todayQuery.data;
  const accountOptions = (referenceResponse?.accounts ?? []).map((account) => ({
    accountId: account.accountId,
    label: account.label,
  }));
  const effectiveAccountId = selectedAccountId ?? accountOptions[0]?.accountId ?? null;
  const todayAccount = todayQuery.data?.accounts.find((a) => a.accountId === effectiveAccountId) ?? null;

  const handleToggleDaily = (entry: ChecklistEntry, dateIso: string, count: number) => {
    completeTask.mutate({
      characterTaskId: entry.characterTaskId,
      periodKey: dateIso,
      count,
      affectedDateIsos: Array.from(new Set([dateIso, todayIso])),
    });
  };

  const handleToggleShared = (entry: ChecklistEntry, count: number) => {
    completeTask.mutate({
      characterTaskId: entry.characterTaskId,
      periodKey: entry.periodKey,
      count,
      affectedDateIsos: Array.from(new Set([...days, todayIso])),
    });
  };

  const handleToggleToday = (entry: ChecklistEntry, count: number) => {
    completeTask.mutate({
      characterTaskId: entry.characterTaskId,
      periodKey: entry.periodKey,
      count,
      affectedDateIsos:
        entry.cadence === 'DAILY' ? [todayIso] : Array.from(new Set([...days, todayIso])),
    });
  };

  if (todayQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading your checklist…</p>;
  }
  if (todayQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load the checklist. Please retry in a moment.
      </p>
    );
  }

  if (accountOptions.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's start checking things off."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  return (
    <div className="stagger-children space-y-5">
      <ChecklistHeader
        accounts={accountOptions}
        selectedAccountId={effectiveAccountId}
        onSelectAccount={setSelectedAccountId}
        weekLabel={formatWeekRange(weekKey)}
        onPrevWeek={() => setWeekKey((k) => shiftWeek(k, -1))}
        onNextWeek={() => setWeekKey((k) => shiftWeek(k, 1))}
        onThisWeek={() => setWeekKey(currentWeekKeyIso)}
        isCurrentWeek={weekKey === currentWeekKeyIso}
        canGoNext={weekKey < currentWeekKeyIso}
      />

      <div className="flex gap-2 md:hidden" role="tablist" aria-label="Checklist view">
        <button
          type="button"
          role="tab"
          aria-selected={mobileTab === 'today'}
          onClick={() => setMobileTab('today')}
          className={tabClasses(mobileTab === 'today')}
        >
          Today
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobileTab === 'week'}
          onClick={() => setMobileTab('week')}
          className={tabClasses(mobileTab === 'week')}
        >
          Week grid
        </button>
      </div>

      {/* Each view is mounted once and shown/hidden with responsive classes (so switching tabs or
          crossing the md breakpoint never re-fetches). Both carry a `region` landmark so tests —
          where jsdom has no real CSS layout — can scope queries to the one that's meant to be
          visible instead of matching duplicate task controls in both. */}
      <div role="region" aria-label="Today view" className={mobileTab === 'today' ? 'block md:hidden' : 'hidden'}>
        <TodayView
          account={todayAccount}
          taskCategoryBySlug={taskCategoryBySlug}
          isPending={todayQuery.isPending}
          isError={todayQuery.isError}
          onToggle={handleToggleToday}
        />
      </div>

      <div role="region" aria-label="Week grid" className={mobileTab === 'week' ? 'block' : 'hidden md:block'}>
        <WeekGrid
          accountId={effectiveAccountId}
          days={dayColumns}
          todayIso={todayIso}
          taskCategoryBySlug={taskCategoryBySlug}
          onToggleDaily={handleToggleDaily}
          onToggleShared={handleToggleShared}
        />
      </div>
    </div>
  );
}
